import * as React from 'react';
import { RouteComponentProps } from '@reach/router';
import { Form as FormikForm, Formik, FormikProps, Field } from 'formik';
import * as yup from 'yup';

import LoadingButton from 'components/LoadingButton';
import { apiRequest } from 'admin/api';

interface MessageTemplate {
  key: string;
  name: string;
  subject: string;
  body: string;
  description: string | null;
  variablesHelp: string | null;
  variables?: Record<string, string>;
}

interface AdminMessageTemplatesPageProps extends RouteComponentProps {}

const AdminMessageTemplatesPage: React.FC<AdminMessageTemplatesPageProps> = () => {
  const [templates, setTemplates] = React.useState<MessageTemplate[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const loadTemplates = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest<{ success: boolean; templates: MessageTemplate[] }>(
        '/message-templates/',
        { method: 'GET' }
      );
      if (response.success) {
        setTemplates(response.templates);
      }
    } catch (e) {
      console.error('Error loading templates', e);
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  return (
    <div>
      <h1>Muokkaa viestipohjia</h1>
      <p className="text-muted">
        Voit muokata automaattisten sähköpostiviestien sisältöä ja otsikkoa.
      </p>

      {loading && <div>Ladataan...</div>}
      {error && <div className="alert alert-danger">Virhe: {error}</div>}

      {templates && (
        <div>
          {templates.map((template) => (
            <TemplateEditForm
              key={template.key}
              template={template}
              onSaved={loadTemplates}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface TemplateEditFormProps {
  template: MessageTemplate;
  onSaved: () => void;
}

const TemplateFormValidationSchema = yup
  .object({
    subject: yup.string().required('Syötä viestin otsikko'),
    body: yup.string().required('Syötä viestin sisältö'),
  })
  .required();

const TemplateEditForm: React.FC<TemplateEditFormProps> = ({ template, onSaved }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const subjectInputRef = React.useRef<HTMLInputElement>(null);
  const bodyTextareaRef = React.useRef<HTMLTextAreaElement>(null);

  const insertVariableAtCursor = (
    fieldName: 'subject' | 'body',
    variable: string,
    formik: FormikProps<{ subject: string; body: string }>
  ) => {
    const inputRef = fieldName === 'subject' ? subjectInputRef : bodyTextareaRef;
    const input = inputRef.current;
    if (!input) return;

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentValue = formik.values[fieldName];
    const variablePlaceholder = `{${variable}}`;

    const newValue =
      currentValue.substring(0, start) +
      variablePlaceholder +
      currentValue.substring(end);

    formik.setFieldValue(fieldName, newValue);

    // Select (highlight) the inserted variable
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start, start + variablePlaceholder.length);
    }, 0);
  };

  return (
    <div className="card mb-4">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">{template.name}</h5>
            {template.description && (
              <small className="text-muted">{template.description}</small>
            )}
          </div>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setExpanded(!expanded)}
            type="button"
          >
            {expanded ? 'Sulje' : 'Muokkaa'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="card-body">
          <Formik
            initialValues={{
              subject: template.subject,
              body: template.body,
            }}
            enableReinitialize={true}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={TemplateFormValidationSchema}
            onSubmit={async (
              values,
              { setErrors, setStatus, setSubmitting },
            ) => {
              setSubmitting(true);
              setSuccessMessage(null);
              setErrorMessage(null);
              try {
                const response = await apiRequest(`/message-templates/${template.key}/`, {
                  method: 'PUT',
                  data: values,
                });
                if (response?.success) {
                  setSuccessMessage('Viestipohja tallennettu!');
                  onSaved();
                  setTimeout(() => setSuccessMessage(null), 3000);
                }
              } catch (e) {
                console.error('Error updating template', e);
                setSubmitting(false);
                setStatus({ success: false });
                setErrorMessage(String(e));
                return;
              }
              setSubmitting(false);
            }}
          >
            {(formik) => (
              <FormikForm>
                <div className="form-group">
                  <label htmlFor="subject">Viestin otsikko</label>
                  <Field name="subject">
                    {({ field, meta }: any) => (
                      <>
                        <input
                          {...field}
                          id="subject"
                          type="text"
                          className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                          disabled={formik.isSubmitting}
                          ref={subjectInputRef}
                        />
                        {meta.touched && meta.error && (
                          <div className="invalid-feedback">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>

                {template.variables && Object.keys(template.variables).length > 0 && (
                  <div className="card bg-light mb-3">
                    <div className="card-body">
                      <h6 className="card-title">Käytettävissä olevat muuttujat</h6>
                      <p className="small text-muted mb-3">
                        Klikkaa muuttujaa lisätäksesi sen kohdistimen kohdalle
                      </p>
                      <div className="d-flex flex-wrap gap-2">
                        {Object.entries(template.variables).map(([varName, varDesc]) => (
                          <button
                            key={varName}
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            style={{
                              cursor: 'pointer',
                              borderRadius: '16px',
                              fontSize: '0.85rem',
                            }}
                            onClick={() => {
                              const activeElement = document.activeElement;
                              const fieldName =
                                activeElement === subjectInputRef.current
                                  ? 'subject'
                                  : 'body';
                              insertVariableAtCursor(fieldName, varName, formik);
                            }}
                            title={varDesc}
                          >
                            {`{${varName}}`}
                          </button>
                        ))}
                      </div>
                      <div className="mt-3">
                        <small className="text-muted">
                          <strong>Selitykset:</strong>
                        </small>
                        {Object.entries(template.variables).map(([varName, varDesc]) => (
                          <div key={varName} className="small text-muted mt-1">
                            <code>{`{${varName}}`}</code> - {varDesc}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="body">Viestin sisältö</label>
                  <Field name="body">
                    {({ field, meta }: any) => (
                      <>
                        <textarea
                          {...field}
                          id="body"
                          className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                          disabled={formik.isSubmitting}
                          rows={15}
                          ref={bodyTextareaRef}
                        />
                        {meta.touched && meta.error && (
                          <div className="invalid-feedback">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>

                {successMessage && (
                  <div className="alert alert-success">{successMessage}</div>
                )}

                {errorMessage && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <div style={{ whiteSpace: 'pre-line' }}>{errorMessage}</div>
                    <button
                      type="button"
                      className="close"
                      aria-label="Close"
                      onClick={() => setErrorMessage(null)}
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                )}

                <div className="d-flex gap-2">
                  <LoadingButton
                    variant="primary"
                    type="submit"
                    loading={formik.isSubmitting}
                  >
                    Tallenna
                  </LoadingButton>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      formik.resetForm();
                      setSuccessMessage(null);
                      setErrorMessage(null);
                      setExpanded(false);
                    }}
                  >
                    Peruuta
                  </button>
                </div>
              </FormikForm>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default AdminMessageTemplatesPage;
